import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router';
import {
  ROI_CALCULATORS,
  ROI_LABELS,
  ROI_LINK_KIND_LABELS,
  ROI_DEFAULT_OPEN_SECTIONS,
  ROI_NUMBER_INPUTS,
  computeRoi,
  isRoiCalculator,
  roiDisplay,
  type RoiInputs,
  type RoiNumberInput,
  type RoiProject as Project,
} from '@wr/shared';
import { ApiError, downloadFile, errorMessage, get } from '../../api.js';
import { Notice, PageHead } from '../../components/ui.js';
import { useSignedIn } from '../../session.js';
import {
  keepProject,
  keptProject,
  pendingFor,
  queueChange,
  sendPending,
  type RoiPendingChange,
} from './data.js';
import { Disclaimer, KeyFigures, Simulator, type RoiFieldValues } from './Simulator.js';

/** How long after the last keystroke a change is sent (the prototype: 700 ms). */
const SAVE_AFTER_MS = 700;
/** While changes wait for the network, how often they are tried again. */
const RETRY_MS = 30_000;

type SaveState = 'saved' | 'saving' | 'waiting' | 'refused';

export interface ProjectContext {
  project: Project;
}

/** The project of the page, for its windows (Delete, Duplicate). */
export function useProject(): ProjectContext {
  return useOutletContext<ProjectContext>();
}

function fieldValues(inputs: RoiInputs): RoiFieldValues {
  const values = { hoursMode: inputs.hoursMode } as RoiFieldValues;
  for (const key of ROI_NUMBER_INPUTS) values[key] = String(inputs[key]);
  return values;
}

/** The value sent for a field as typed: '' for an empty field; nothing while it is not a number yet. */
function parsed(field: RoiNumberInput, text: string): number | '' | null {
  if (text.trim() === '' && field !== 'robotKgPerPick') return '';
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

/**
 * An ROI project (brief section 7.7): the simulator, saved as the person
 * types when it is theirs (or they are an admin). A colleague's project
 * opens too: its figures can be changed to show them, never saved; Duplicate
 * makes a copy of one's own.
 */
export function RoiProject({ present = false }: { present?: boolean }) {
  const { id = '' } = useParams();
  // Another project starts from a clean page.
  return <ProjectPage key={id} id={id} present={present} />;
}

/**
 * Show to client (brief section 7.7): the simulator alone, for a screen
 * share or an iPad: logo, client, scenario, the three key figures live, the
 * disclaimer and the sections; no menu, link, author, PDF, package or
 * notification. Figures are saved live as on the project page; the
 * sections start as the prototype opens them (1 to 5), and opening or
 * closing one here is not saved. A discreet Exit; never full screen by itself.
 */
function usePresenting(present: boolean) {
  useEffect(() => {
    if (!present) return;
    document.body.classList.add('presenting');
    window.scrollTo(0, 0);
    return () => document.body.classList.remove('presenting');
  }, [present]);
}

function ProjectPage({ id, present }: { id: string; present: boolean }) {
  usePresenting(present);
  const navigate = useNavigate();
  const location = useLocation();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const { user, refresh } = useSignedIn();
  const [project, setProject] = useState<Project | null>(null);
  const [values, setValues] = useState<RoiFieldValues | null>(null);
  const [openSections, setOpenSections] = useState<number[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fromDevice, setFromDevice] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [refused, setRefused] = useState<string | null>(null);
  const change = useRef<RoiPendingChange | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let current = true;
    const show = async (loaded: Project) => {
      // Changes made on this device and not sent yet stay on screen.
      const waiting = await pendingFor(user.id, loaded.id);
      if (!current) return;
      setProject(loaded);
      setValues(fieldValues({ ...loaded.inputs, ...waiting?.inputs }));
      setOpenSections(
        present ? ROI_DEFAULT_OPEN_SECTIONS : (waiting?.openSections ?? loaded.openSections),
      );
      if (waiting) setSaveState('waiting');
    };
    get<Project>(`/roi/projects/${encodeURIComponent(id)}`)
      .then(async (loaded) => {
        await keepProject(user.id, loaded);
        await show(loaded);
        // Changes waiting from an earlier visit go now.
        if (await pendingFor(user.id, loaded.id)) {
          const out = await sendPending(user.id, loaded.id);
          if (current && out.status === 'sent') setSaveState('saved');
        }
      })
      .catch(async (error: unknown) => {
        if (error instanceof ApiError && error.status === 401) return void refresh();
        const offline =
          error instanceof ApiError && (error.code === 'offline' || error.status >= 500);
        const kept = offline ? await keptProject(user.id, id) : null;
        if (!current) return;
        if (kept) {
          setFromDevice(true);
          await show(kept);
        } else if (error instanceof ApiError && error.status === 404) {
          setLoadError('This project does not exist, or it was deleted.');
        } else {
          setLoadError(errorMessage(error));
        }
      });
    return () => {
      current = false;
    };
  }, [id, user.id, refresh, present]);

  const send = useCallback(async () => {
    timer.current = null;
    const next = change.current;
    change.current = null;
    if (next) await queueChange(user.id, id, next);
    const out = await sendPending(user.id, id);
    if (change.current) return; // a newer change is on its way
    if (out.status === 'sent') {
      setSaveState('saved');
      setFromDevice(false);
    } else if (out.status === 'waiting') {
      setSaveState('waiting');
    } else {
      setSaveState('refused');
      setRefused(
        out.error.status === 404
          ? 'This project was deleted meanwhile: your last changes were not saved.'
          : `Your last changes were not saved. ${errorMessage(out.error)}`,
      );
    }
  }, [id, user.id]);

  // A change typed just before leaving the page is still sent.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        void send();
      }
    },
    [send],
  );

  // Waiting for the network: try again when it returns, and every 30 seconds.
  useEffect(() => {
    if (saveState !== 'waiting') return;
    const retry = () => void send();
    window.addEventListener('online', retry);
    const interval = setInterval(retry, RETRY_MS);
    return () => {
      window.removeEventListener('online', retry);
      clearInterval(interval);
    };
  }, [saveState, send]);

  function queue(update: Partial<RoiPendingChange>) {
    if (!project?.canChange) return;
    change.current = {
      inputs: { ...change.current?.inputs, ...update.inputs },
      openSections: update.openSections ?? change.current?.openSections,
      changedAt: new Date().toISOString(),
    };
    setSaveState('saving');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void send(), SAVE_AFTER_MS);
  }

  const display = useMemo(() => {
    if (!project || !values || !isRoiCalculator(project.calculator)) return null;
    return roiDisplay(computeRoi(values), project.calculator);
  }, [project, values]);

  if (loadError) {
    return (
      <>
        <PageHead
          title="ROI simulator"
          actions={
            <Link className="btn sm" to="/roi">
              All projects
            </Link>
          }
        />
        <Notice kind="error">{loadError}</Notice>
      </>
    );
  }
  if (!project || !values || !display || !isRoiCalculator(project.calculator)) {
    return <div className="spinner" aria-label="Loading" />;
  }

  const calculator = ROI_CALCULATORS[project.calculator];
  const simulator = (
    <Simulator
      calculator={project.calculator}
      values={values}
      display={display}
      openSections={openSections}
      onToggle={(section, open) => {
        const next = open
          ? [...new Set([...openSections, section])].sort((a, b) => a - b)
          : openSections.filter((s) => s !== section);
        setOpenSections(next);
        if (!present) queue({ openSections: next });
      }}
      onChange={(field, text) => {
        setValues({ ...values, [field]: text });
        if (field === 'hoursMode') {
          queue({ inputs: { hoursMode: text === 'total' ? 'total' : 'weekly' } });
          return;
        }
        const value = parsed(field, text);
        if (value !== null) queue({ inputs: { [field]: value } });
      }}
    />
  );

  if (present) {
    // Exit goes back to the project page (Back does the same).
    const exit = () =>
      location.key !== 'default' ? navigate(-1) : navigate(`/roi/${id}`, { replace: true });
    return (
      <>
        <div className="pres-head">
          <img src="/logo-rail.png" alt="Waste Robotics" className="pres-logo" />
          <div className="pres-title">
            <b>{project.link.company}</b>
            <span>{project.scenarioName}</span>
          </div>
          <button type="button" className="pres-exit" onClick={exit}>
            Exit
          </button>
        </div>
        <KeyFigures calculator={project.calculator} display={display} />
        {saveState === 'refused' && refused && <Notice kind="error">{refused}</Notice>}
        <Disclaimer labels={ROI_LABELS[calculator.lang]} />
        {simulator}
      </>
    );
  }

  /** The PDF as saved: what waits on this device is sent first. */
  async function exportPdf() {
    setExporting(true);
    setExportError(null);
    try {
      if (timer.current) {
        clearTimeout(timer.current);
        await send();
      }
      await downloadFile(`/roi/projects/${encodeURIComponent(id)}/pdf`);
    } catch (error) {
      setExportError(
        error instanceof ApiError && error.code === 'offline'
          ? 'The PDF needs the network: try again when you are online.'
          : errorMessage(error),
      );
    } finally {
      setExporting(false);
    }
  }
  const labels = ROI_LABELS[calculator.lang];
  const link = project.link;
  const typed = link.kind === 'company' && !link.hubspotId && !link.portalId;
  const owner = project.createdBy.name ?? 'a former user';
  const status = !project.canChange
    ? 'Not saved'
    : {
        saved: 'Saved',
        saving: 'Saving…',
        waiting: 'Waiting for the network',
        refused: 'Not saved',
      }[saveState];

  return (
    <>
      <KeyFigures calculator={project.calculator} display={display} />
      <PageHead
        title="ROI simulator"
        actions={
          <span className="actions">
            <Link className="btn sm primary" to="present">
              Show to client
            </Link>
            <Link className="btn sm" to="/roi">
              All projects
            </Link>
          </span>
        }
      />
      <div className="crd-head">
        <div>
          <b>{link.company}</b>
          <span>{project.scenarioName}</span>
        </div>
        <span className="badge b-lead">{calculator.name}</span>
        <span className="note" role="status">
          {status}
        </span>
      </div>
      {!project.canChange && (
        <Notice>
          This project is {owner}&rsquo;s. You can change the figures to show them, but they are not
          saved. Duplicate it to keep your own copy.
        </Notice>
      )}
      {fromDevice && (
        <Notice kind="warn">
          No connection: this is the copy kept on this device. Your changes are sent when the
          network returns.
        </Notice>
      )}
      {saveState === 'waiting' && !fromDevice && project.canChange && (
        <Notice kind="warn">
          Your last changes are kept on this device and sent when the network returns.
        </Notice>
      )}
      {saveState === 'refused' && refused && <Notice kind="error">{refused}</Notice>}
      <div className="lp">
        <span className="f">Linked to</span>
        <div className="lp-sel">
          <div className="lp-chip">
            <span className={`lp-k ${link.kind}`}>{ROI_LINK_KIND_LABELS[link.kind]}</span>
            <span>
              <span className="tt">{link.label}</span>
              <small>{typed ? 'Company name as typed' : link.company}</small>
            </span>
          </div>
        </div>
      </div>
      <Disclaimer labels={labels} />
      {simulator}
      <div className="actions roi-actions">
        {project.canChange && (
          <Link className="btn danger" to="delete">
            Delete
          </Link>
        )}
        <button type="button" className="btn" disabled={exporting} onClick={() => void exportPdf()}>
          {exporting ? 'Preparing the PDF' : 'PDF'}
        </button>
        <Link className="btn" to="duplicate">
          Duplicate
        </Link>
      </div>
      {exportError && <Notice kind="error">{exportError}</Notice>}
      <Outlet context={{ project } satisfies ProjectContext} />
    </>
  );
}
